import { useEffect } from "react";
import { Box } from "@mui/material";
import { useParams, useLocation } from "react-router-dom";

import TabPanel from "../../../../shared/UI/TabPanel";

import AdvertisementCol from "../../../Ads/components/AdvertisementCol";
import { useEntityFollow } from "../../../Entity/api/mutations/useEntityFollow";
import { useEntityDetails } from "../../../Entity/api/queries/useEntityDetails";
import { useEntityDataFormatter } from "../../../Entity/hooks/useEntityDataFormatter";
import { useEntityVisitMutation } from "../../../Entity/api/mutations/useVisitMutation";

import useIsOwnProfile from "../../utils/useIsOwnProfile";

import Heading from "../../components/Heading";
import NetworkTab from "../../components/NetworkTab";
import ActivityTab from "../../components/ActivityTab";
import GoServiceTab from "../../components/GoServiceTab";
import TabsNavigation from "../../components/TabsNavigation";

import HomeTab from "./components/HomeTab";
import GroupsTab from "./components/GroupsTab";

const EntityProfile = () => {
  const { entityId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "home";

  const { data: entityData, isLoading: isEntityLoading, isError: isEntityError } = useEntityDetails(entityId!);
  const visitMutation = useEntityVisitMutation();
  const { follow, unfollow, isFollowing: isFollowLoading } = useEntityFollow(Number(entityId));

  const { isOwn, isLoading: isOwnLoading } = useIsOwnProfile({
    entityId: Number(entityId),
    isEntityIdLoading: isEntityLoading,
  });

  useEffect(() => {
    if (isOwn) return;
    visitMutation.mutate(entityId!);
  }, [entityId]);

  const { fullName, firstTwoChr, countryName, role, cityName, media, isFollowing } = useEntityDataFormatter(entityData);

  const handleFollowToggle = () => {
    if (isFollowing) {
      unfollow();
    } else {
      follow();
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.entityProfileContainer}>
        <Heading
          fullName={fullName}
          firstTwoChr={firstTwoChr}
          countryName={countryName}
          role={role}
          cityName={cityName}
          media={media}
          isLoading={isEntityLoading || isFollowLoading || isOwnLoading}
          isFollowing={isFollowing}
          onFollowToggle={handleFollowToggle}
          isEntityOwnProfile={isOwn}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
              overflowX: "auto",
              whiteSpace: "nowrap",
              padding: { xs: "0 8px 8px", sm: "0" },
            }}
          >
            <TabsNavigation isOwn={isOwn} />
          </Box>
        </Heading>

        <TabPanel value={currentTab} tab="home">
          <HomeTab entity={entityData} isLoading={isEntityLoading} />
        </TabPanel>
        <TabPanel value={currentTab} tab="network">
          <NetworkTab entityId={String(entityId)} />
        </TabPanel>
        <TabPanel value={currentTab} tab="activity">
          <ActivityTab userId={String(entityData?.userId)!} fullName={fullName} />
        </TabPanel>
        <TabPanel value={currentTab} tab="goService">
          <GoServiceTab
            fullName={fullName}
            userId={String(entityData?.userId!)}
            isOwn={isOwn}
            isDataProfileDataLoading={isEntityLoading || isOwnLoading}
          />
        </TabPanel>
        {isOwn && (
          <TabPanel value={currentTab} tab="groups">
            <GroupsTab />
          </TabPanel>
        )}
      </Box>
      <Box sx={styles.advertisementContainer}>
        <AdvertisementCol />
      </Box>
    </Box>
  );
};

export default EntityProfile;

const styles = {
  container: { display: "flex", gap: "16px" },
  entityProfileContainer: { width: { xs: "100%", lg: "636px" }, display: "flex", flexDirection: "column", gap: "16px" },
  advertisementContainer: { display: { xs: "none", lg: "block" } },
};
